<template>
  <!-- A person's profile: who they are, their recent work and, on your own profile, your workspaces (US 9391) -->
  <div class="pf-page">
    <section class="home-hero pf-hero" qid="profile-hero">
      <div class="pf-hero__photo">
        <span class="pf-avatar" :style="{ backgroundImage: 'url(' + avatarUrl + ')' }" qid="profile-photo"></span>
      </div>

      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ isMyProfile ? $t('profile_page.eyebrow_own') : $t('profile_page.eyebrow') }}</div>
        <h1 class="home-hero__title" qid="profile-name">{{ user.name }}</h1>
        <div class="pf-meta">
          <span v-if="user.email" qid="profile-email"><font-awesome-icon icon="envelope"/>{{ user.email }}</span>
          <span v-if="user.phone" qid="profile-phone"><font-awesome-icon icon="phone"/>{{ user.phone }}</span>
        </div>
        <div v-if="canEdit" class="home-hero__chips">
          <button type="button"
                  class="home-chip home-chip--light"
                  qid="edit-profile-button"
                  @click="$refs.editPanel.show(user)">
            <font-awesome-icon icon="pen"/>{{ $t('profile_page.edit_profile') }}
          </button>
        </div>
      </div>

      <div v-if="isMyProfile && summary.workspaces" class="pf-stats" qid="profile-stats">
        <div class="pf-stat">
          <b>{{ summary.workspaces.length }}</b>
          <span>{{ $t('profile_page.stats.workspaces') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ summary.open_tasks }}</b>
          <span>{{ $t('profile_page.stats.open_tasks') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ summary.changes_30_days }}</b>
          <span>{{ $t('profile_page.stats.changes') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ lastLoginDay }}</b>
          <span>{{ lastLoginTime ? $t('profile_page.stats.last_sign_in_at', { time: lastLoginTime }) : $t('profile_page.stats.last_sign_in') }}</span>
        </div>
      </div>
    </section>

    <div class="pf-cols">
      <div class="v2-card pf-activity" qid="profile-activity">
        <div class="v2-card__head">
          <h3>
            {{ $t('profile_page.activity.title') }}
            <span v-if="getClient" class="v2-card__sub">· {{ $t('profile_page.activity.in', { name: getClient.name }) }}</span>
          </h3>
        </div>
        <div class="pf-activity__feed">
          <ActivityFeed v-if="user.id"
                        :user="user.id"
                        area-filter
                        :reload-key="activityKey"
                        :empty-text="$t('profile_page.activity.empty')"
          />
        </div>
      </div>

      <div v-if="isMyProfile" class="pf-side">
        <div v-if="summary.workspaces" class="v2-card" qid="profile-workspaces">
          <div class="v2-card__head">
            <h3>{{ $t('profile_page.workspaces.title') }} <span class="v2-card__sub">· {{ summary.workspaces.length }}</span></h3>
          </div>
          <div class="v2-card__body">
            <div v-for="workspace in summary.workspaces"
                 :key="workspace.id"
                 class="pf-ws"
                 qid="profile-workspace">
              <span class="pf-ws__logo">{{ initials(workspace.name) }}</span>
              <div class="pf-ws__text">
                <div class="pf-ws__name">{{ workspace.name }}</div>
                <div class="pf-ws__sub">
                  {{ workspace.parent_name ? $t('profile_page.workspaces.partition_of', { name: workspace.parent_name }) : $t('profile_page.workspaces.client') }}
                  · {{ $t('profile_page.workspaces.emails', { mode: $t('client_page.notifications.mode.' + (workspace.email_mode || 'off')) }) }}
                </div>
              </div>
              <span v-if="workspace.role"
                    class="v2-tag"
                    :class="roleTag(workspace.role)">
                {{ $t('maintenance.users.role.' + $options.filters.slugify(workspace.role)) }}
              </span>
            </div>
          </div>
        </div>

        <div class="v2-card" qid="profile-sign-in-card">
          <div class="v2-card__head"><h3>{{ $t('profile_page.sign_in.title') }}</h3></div>
          <div class="v2-card__body">
            <dl class="pf-kv">
              <dt>{{ $t('profile_page.sign_in.method') }}</dt>
              <dd>
                <SignInMethodIcon :method="user.authentication || 'Application'"/>
                {{ $t('users_page.sign_in.' + (user.authentication || 'Application')) }}
              </dd>
              <dt>{{ $t('profile_page.sign_in.last') }}</dt>
              <dd>{{ summary.last_login_at ? formatDateTime(summary.last_login_at) : $t('profile_page.sign_in.never') }}</dd>
              <dt>{{ $t('profile_page.sign_in.member_since') }}</dt>
              <dd>{{ summary.created_at ? formatDate(summary.created_at) : '–' }}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <ProfileEditPanel ref="editPanel" @saved="profileSaved"/>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import {config} from '@/config.js';
  import moment from 'moment';
  import ActivityFeed from '@/components/activity/feed/ActivityFeed.vue';
  import ProfileEditPanel from '@/components/user/ProfileEditPanel.vue';
  import SignInMethodIcon from '@/components/user/SignInMethodIcon.vue';

  const ROLE_TAGS = {
    'Client admin': 'v2-tag--violet',
    'Partition admin': 'v2-tag--violet',
    'Validator': 'v2-tag--blue'
  };

  export default {
    name: 'Profile',
    components: {
      ActivityFeed,
      ProfileEditPanel,
      SignInMethodIcon
    },
    data() {
      return {
        user: {},
        summary: {},
        activityKey: 0
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      },
      isMyProfile() {
        return !!this.user.id && !!this.getUser && this.user.id === this.getUser.id;
      },
      canEdit() {
        return this.isMyProfile && this.isGranted([this.permissions.USER_EDIT, this.permissions.USER_EDIT_OWN], this.user);
      },
      avatarUrl() {
        return this.user.id ? userAvatarUrl(this.user, true) : '';
      },
      lastLoginDay() {
        if (!this.summary.last_login_at) {
          return '–';
        }
        let date = moment(this.summary.last_login_at);
        if (date.isSame(moment(), 'day')) {
          return this.$t('profile_page.today');
        }
        if (date.isSame(moment().subtract(1, 'day'), 'day')) {
          return this.$t('profile_page.yesterday');
        }
        return date.format(config.dateFormat);
      },
      lastLoginTime() {
        return this.summary.last_login_at ? moment(this.summary.last_login_at).format('HH:mm') : '';
      }
    },
    watch: {
      getClient(client) {
        if (client) {
          this.loadUser();
        }
      },
      '$route.params.slug'() {
        this.loadUser();
      }
    },
    methods: {
      loadUser() {
        piincHttp.get('users/' + this.$route.params.slug).then(response => {
          this.user = response.data;
          this.resetActivity();
          this.loadSummary();
        }, function() {});
      },
      loadSummary() {
        piincHttp.get('users/' + this.user.slug + '/profile-summary').then(response => {
          this.summary = response.data;
        }, function() {});
      },
      resetActivity() {
        this.activityKey += 1;
      },
      formatDate(date) {
        return moment(date).format(config.dateFormat);
      },
      formatDateTime(date) {
        return moment(date).format(config.dateFormat + ' HH:mm');
      },
      initials(name) {
        let words = (name || '').trim().split(/\s+/).filter(Boolean);
        return words.slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
      },
      roleTag(role) {
        return ROLE_TAGS[role] || 'v2-tag--grey';
      },
      profileSaved(user) {
        user.clients = this.getUser.clients;
        this.$store.commit('storeUser', user);
        this.user = Object.assign({}, this.user, user);

        if (user.slug !== this.$route.params.slug) {
          this.$router.push({ name: 'profile', params: { slug: user.slug } });
        }
      }
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.loadUser();
      }
    }
  }
</script>
